import { QuestionTypeEnum, QuestionTypeV2Enum, TestModeEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Editor, Point, Range } from 'slate';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { SlatePlugin } from '../../../slatePlugins/slatePlugins.types';
import { Nothing } from '../../BlogEditor/shared-components/Nothing';
import { QuestionDisplayMode } from '../../CircleText/CircleText';
import { figmaColors } from '../../colors';
import { colorsV2 } from '../../colors-v2';
import Blank, { BlankChange } from '../DoTest/Blank';
import { BlankDash } from '../DoTest/BlankDash';
import { BLANK_TYPE, BlankItem } from '../type';
import AdminBlank from './Blank';
import { QuestionV2 } from '../../Question/QuestionV2';
import { DropBlankDash } from '../DoTest/DropBlankDash';

const BlankContainer = styled.span`
  display: inline-flex;
  line-height: 28px;
  margin-left: 8px;
  .input-display {
    user-select: none;
    line-height: 28px;
    display: inline-flex;
    align-items: center;
    color: ${figmaColors.text1};
    box-decoration-break: clone;
    border-radius: 8px;
    .text {
      color: ${colorsV2.black100};
      font-weight: 600;
      font-size: 14px;
      display: flex;
      align-items: center;
      line-height: 24px;
      border-bottom: 2px solid ${colorsV2.gray20};
    }
    .circle-text {
      background: ${colorsV2.black100};
      > span {
        color: ${colorsV2.white100};
      }
      font-size: 12px;
      line-height: 14px;
      margin-right: 8px;
      &.hidden {
        display: none;
      }
    }
    .circle-text.selected {
      background: ${colorsV2.blue100};
    }
    .dash {
      display: none;
      margin-right: 8px;
      border-bottom: 2px solid ${colorsV2.gray10};
      width: 52px;
      height: 16px;
    }
    .delete-btn {
      font-size: 12px;
      border-radius: 50%;
      cursor: pointer;
      line-height: 12px;
      color: ${colorsV2.red100};
      &:hover {
        color: ${figmaColors.primary6};
      }
    }

    .ic-wrapper {
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.disableResult {
      .ic-wrapper {
        color: ${colorsV2.gray20};
        margin-left: 2px;
      }
      .textarea {
        input {
          border-bottom: 2px solid ${colorsV2.black10};
        }
      }
    }

    &.${TestModeEnum.ADMIN_SETUP} {
      .dash {
        display: block;
      }
    }

    &.focused {
      .input-display {
        border-color: ${figmaColors.primary6};
      }
    }
  }
`;

interface OldBlankPluginOptions {
  blanks: BlankItem[];
  onRemoveBlank: (blank: any, editorId?: any) => void;
}

const RemoveKeys = {
  BACKSPACE: 'Backspace',
  DELETE: 'Delete',
} as const;

export const OldBlankPlugin = (options: OldBlankPluginOptions): SlatePlugin => {
  const { blanks, onRemoveBlank } = options;
  return {
    voidTypes: [BLANK_TYPE],
    inlineTypes: [BLANK_TYPE],
    renderElement: (renderProps: RenderElementProps) => {
      const { element, attributes, children } = renderProps;
      if (element.type === BLANK_TYPE) {
        const found = blanks.find((blank: BlankItem) => blank.id === element.id);
        if (found) {
          return (
            <BlankContainer {...attributes} key={found.key + found.id}>
              <AdminBlank contentEditable={false} blank={found} onRemoveBlank={onRemoveBlank} />
              {children}
            </BlankContainer>
          );
        }
      }

      return null;
    },
    onKeyDown: (e, editor: any) => {
      /**
       * if range is collapsed -> check press on backspace/Delete
       *     call onRemoveBlank
       * if range is expanded -> on every key pressed
       *     check inside the selection have any blanks
       *        if have blanks -> call onRemoveBlank
       */
      if (Range.isCollapsed(editor.selection)) {
        /**
         * Based on what key user press, nearestBlankPoint is the point before/after the cursor
         */
        let nearestBlankPoint: Point;
        if (e.key === RemoveKeys.BACKSPACE)
          nearestBlankPoint = Editor.before(editor, editor.selection);
        else if (e.key === RemoveKeys.DELETE)
          nearestBlankPoint = Editor.after(editor, editor.selection);

        if (nearestBlankPoint) {
          const matchNodes = Array.from(
            Editor.nodes(editor, {
              at: nearestBlankPoint,
              mode: 'lowest',
              match: editor.isBlank,
            })
          );
          if (matchNodes.length) {
            // this mean has a blank before
            const [[blank]] = matchNodes as any;
            const removeCandidate = blanks.find((bl: any) => bl.id === blank.id);
            onRemoveBlank(removeCandidate);
          }
        }
      } else {
        const matchNodes = Array.from(
          Editor.nodes(editor, {
            at: editor.selection,
            mode: 'lowest',
            match: editor.isBlank,
          })
        ) as any;
        if (matchNodes.length) {
          matchNodes.forEach(([{ id }]) => {
            const removeCandidate = blanks.find((bl) => bl.id === id);
            if (removeCandidate) {
              onRemoveBlank(removeCandidate);
            }
          });
        }
      }
    },
  };
};

export interface OldBlankDoTestPluginProps {
  no: number;
  blanks: BlankItem[];
  blankValues?: BlankChange[];
  useBlankDash?: boolean;
  showQuestionNo?: boolean;
  showBlankBackground?: boolean;
  withQuestionNoOutsideBlankBlock?: boolean;
  useDropBlankDash?: boolean;
  questionType?: QuestionTypeEnum | QuestionTypeV2Enum;
  dropdownOptions?: {
    key: number;
    text: string;
  }[];
  onBlankChange?: (input: BlankChange) => void;
  onRemove?: (blankId) => void;
  onDropdownSelect?: (input) => void;
}
export const OldBlankDoTestPlugin = (options: OldBlankDoTestPluginProps): SlatePlugin => {
  return {
    inlineTypes: [BLANK_TYPE],
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;
      const { blanks } = options;
      const blank = blanks?.find((b: BlankItem) => b.id === element.id);

      if (blank && element?.type === BLANK_TYPE) {
        return <DoTestElement blank={blank} renderProps={renderProps} options={options} />;
      }
      return null;
    },
  };
};

const DoTestElement = observer(
  ({
    renderProps,
    options,
    blank,
  }: {
    options: OldBlankDoTestPluginProps;
    renderProps: RenderElementProps;
    blank: any;
  }) => {
    const { attributes, children, element } = renderProps;
    const {
      no,
      blanks,
      blankValues,
      useBlankDash,
      showQuestionNo,
      showBlankBackground,
      withQuestionNoOutsideBlankBlock,
      useDropBlankDash,
      dropdownOptions,
      onBlankChange,
      onRemove,
      onDropdownSelect,
      ...rest
    } = options;
    const val = blank ? blankValues.find((value: any) => value.blankId === blank.id) : null;

    if (useDropBlankDash) {
      return (
        <BlankContainer
          style={{ userSelect: 'none', margin: '0px' }}
          {...attributes}
          contentEditable={false}
        >
          <DropBlankDash
            no={no + blank.key}
            dropId={blank?.id}
            text={val?.text}
            dropdownOptions={dropdownOptions}
            onRemove={() => onRemove?.(blank.id)}
            onDropdownSelect={onDropdownSelect}
          />
          {children}
        </BlankContainer>
      );
    }

    if (useBlankDash) {
      return (
        <BlankContainer
          style={{ display: 'inline-block', userSelect: 'none' }}
          {...attributes}
          contentEditable={false}
        >
          <BlankDash viewMode={QuestionDisplayMode.QUESTION_DO_TEST} no={no + blank.key} />
          {children}
        </BlankContainer>
      );
    }
    return (
      <BlankContainer
        style={{
          display: withQuestionNoOutsideBlankBlock ? 'inline-flex' : 'inline-block',
          userSelect: 'none',
        }}
        {...attributes}
        onMouseDown={(e) => e.preventDefault()}
        contentEditable={false}
        className="sentence-blank"
      >
        {withQuestionNoOutsideBlankBlock && (
          <QuestionV2 no={no + blank.key} noArrow withDot marginRight={0} />
        )}
        <Blank
          showRemoveButton={false}
          onChange={onBlankChange}
          no={no + blank.key}
          id={element.id}
          value={val?.text}
          showQuestionNo={showQuestionNo}
          showBlankBackground={showBlankBackground}
          withQuestionNoOutsideBlankBlock={withQuestionNoOutsideBlankBlock}
          {...rest}
        />
        <Nothing element={element} attributes={attributes}>
          {children}
        </Nothing>
      </BlankContainer>
    );
  }
);
